import React from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { ProtectedMentorshipRoute } from "@/components/ProtectedRoute";
import { AuthProvider } from "@/components/AuthProvider";
import NotFound from "@/pages/not-found";
import Home from "@/pages/Home";
import PremiumContent from "@/pages/PremiumContent";
import Slideshow from "@/pages/Slideshow";
import FreePeptides from "@/pages/FreePeptides";
import LibraryPage from "@/pages/LibraryPage";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import PostPaymentSignup from "@/pages/PostPaymentSignup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Longevity2025 from "@/pages/Longevity2025";
import BryanJohnsonReview from "@/pages/BryanJohnsonReview";
import LongevityMistakes from "@/pages/LongevityMistakes";
import LongevityLessons from "@/pages/LongevityLessons";
import PeptideProtocols from "@/pages/PeptideProtocols";
import PeptideProtocolsModular from "@/pages/PeptideProtocolsModular";
import PeptideAssistant from "@/pages/PeptideAssistant";
import Mentorship from "@/pages/Mentorship";
import SampleMaleHRT from "@/pages/SampleMaleHRT";
import MaleHRTJournal from "@/pages/MaleHRTJournal";
import { MaleHRTExample } from "@/pages/MaleHRTExample";
import LongevityGuild from "@/pages/LongevityGuild";
import BusinessSolutions from "@/pages/BusinessSolutions";
import Consulting from "@/pages/Consulting";
import ProposalPDF from "@/pages/ProposalPDF";
import PLVProposal from "@/pages/PLVProposal";
import CactusProposal from "@/pages/CactusProposal";
import HighMountMadisonProposal from "@/pages/HighMountMadisonProposal";
import NextHealthProposal from "@/pages/NextHealthProposal";
import Levey052025 from "@/pages/levey052025";
import PatientOnboarding from "@/pages/PatientOnboarding";
import PatientOnboardingBasic from "@/pages/PatientOnboardingBasic";
import PatientOnboardingFF from "@/pages/PatientOnboardingFF";
import MentorshipContent from "@/pages/MentorshipContent";
import ProposalsPage from "@/pages/Proposals";
import PatientsPage from "@/pages/Patients";
import FutureExecutiveHealth from "@/pages/lectures/FutureExecutiveHealth";

import QAPeptideProtocols from "@/pages/mentorship/QAPeptideProtocols";
import CortisolMolecularFoundations from "@/pages/mentorship/lectures/CortisolMolecularFoundations";
import QuestioningGoldStandard from "@/pages/mentorship/lectures/QuestioningGoldStandard";
import WhatIsLongevityMedicine from "@/pages/mentorship/lectures/WhatIsLongevityMedicine";
import CellularBasisOfHealth from "@/pages/mentorship/lectures/CellularBasisOfHealth";
import MitochondrialHealth from "@/pages/mentorship/lectures/MitochondrialHealth";
import EpigeneticsCellularMedicine from "@/pages/mentorship/lectures/EpigeneticsCellularMedicine";
import PeroxisomesHealthDisease from "@/pages/mentorship/lectures/PeroxisomesHealthDisease";
import HormonesCellularFundamentals from "@/pages/mentorship/lectures/HormonesCellularFundamentals";
import TestosteroneMolecularFoundations from "@/pages/mentorship/lectures/TestosteroneMolecularFoundations";
import TestosteroneClinicalApplications from "@/pages/mentorship/lectures/TestosteroneClinicalApplications";
import EstrogenMolecularFoundations from "@/pages/mentorship/lectures/EstrogenMolecularFoundations";
import EstrogenClinicalApplications from "@/pages/mentorship/lectures/EstrogenClinicalApplications";
import ThyroidMolecularClinical from "@/pages/mentorship/lectures/ThyroidMolecularClinical";
import EHRPlatformsForLongevity from "@/pages/mentorship/lectures/EHRPlatformsForLongevity";
import GLP1Agonists from "@/pages/mentorship/lectures/GLP1Agonists";
import Inflammation from "@/pages/mentorship/lectures/Inflammation";
import NACClinicalApplication from "@/pages/mentorship/lectures/NACClinicalApplication";
import MovementNutritionSleep from "@/pages/mentorship/lectures/MovementNutritionSleep";
import AnatomyOfALongevityConsult from "@/pages/mentorship/lectures/AnatomyOfALongevityConsult";
import WomensLongevityInterventions from "@/pages/mentorship/lectures/WomensLongevityInterventions";
import EvaluatingBusinessOpportunities from "@/pages/mentorship/lectures/EvaluatingBusinessOpportunities";
import CellularFrameworkLongevity from "@/pages/mentorship/lectures/CellularFrameworkLongevity";
import ShiftFrameworkHealthLongevity from "@/pages/mentorship/lectures/ShiftFrameworkHealthLongevity";
import LifestyleFoundations from "@/pages/mentorship/lectures/LifestyleFoundations";
import LifestyleFoundations2 from './pages/mentorship/lectures/LifestyleFoundations2';
import LifestyleFoundations3 from './pages/mentorship/lectures/LifestyleFoundations3';
import LifestyleFoundations4 from "@/pages/mentorship/lectures/LifestyleFoundations4";
import LifestyleFoundations5 from "@/pages/mentorship/lectures/LifestyleFoundations5";


import { CardiovascularDiseaseLongevity } from "@/pages/mentorship/lectures/CardiovascularDiseaseLongevity";
import Exosomes from "@/pages/mentorship/lectures/Exosomes";

import LongCovid from "@/pages/mentorship/lectures/LongCovid";
import NeurodegenerativeDisease from "@/pages/mentorship/lectures/NeurodegenerativeDisease";
import AdvancedTherapeuticInnovations from "@/pages/mentorship/lectures/AdvancedTherapeuticInnovations";
import { BrainHealthNeurofeedback } from "@/pages/mentorship/lectures/BrainHealthNeurofeedback";
import AdvancedDiagnostics from "@/pages/mentorship/lectures/AdvancedDiagnostics";
import { CellularBasisHealthDisease } from "@/pages/mentorship/lectures/CellularBasisHealthDisease";
import FunctionalAdvancedLabTesting from "@/pages/mentorship/lectures/FunctionalAdvancedLabTesting";
import LifestyleInterventions from "@/pages/mentorship/lectures/LifestyleInterventions";
import PharmaceuticalsRepurposedMedications from "@/pages/mentorship/lectures/PharmaceuticalsRepurposedMedications";
import MicrodosingGLP1s from "@/pages/mentorship/lectures/MicrodosingGLP1s";
import MicrodosingGLP1s2 from "@/pages/mentorship/lectures/MicrodosingGLP1s2";
import SupplementsNutraceuticals from "@/pages/mentorship/lectures/SupplementsNutraceuticals";
import PeptidesShiftApproach from "@/pages/mentorship/lectures/PeptidesShiftApproach";
import TakingInvestmentSellingPractice from "@/pages/mentorship/lectures/TakingInvestmentSellingPractice";
import SemaglutideMechanismPrecisionUpdate from "@/pages/mentorship/lectures/SemaglutideMechanismPrecisionUpdate";
import MeasuringSenescenceClinically from "@/pages/mentorship/lectures/MeasuringSenescenceClinically";

import GLP1sAndLongevityAtriaV3 from "@/pages/mentorship/lectures/GLP1sAndLongevityAtriaV3";
import LectureGenerator from "@/pages/mentorship/LectureGenerator";
import PromotionPayment from "@/pages/mentorship/payment/Promotion";
import LectureManager from "@/pages/mentorship/admin/LectureManager";
import TestosteroneEmbedExample from "@/pages/mentorship/lectures/TestosteroneEmbedExample";
import LectureStudio from "@/pages/mentorship/LectureStudio";

import PracticeModels from "@/pages/mentorship/business/practice-models";
import Telehealth from "@/pages/mentorship/business/telehealth";
import EHRPlatforms from "@/pages/mentorship/business/ehr-platforms";
import InsulinResistance from "@/pages/mentorship/longevity/insulin-resistance";
import Promotion0625 from "@/pages/mentorship/payment/Promotion0625";

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/patients" component={PatientsPage} />
      <Route path="/peptides" component={FreePeptides} />
      <Route path="/library" component={LibraryPage} />
      <Route path="/library/:category" component={LibraryPage} />
      <Route path="/library/:category/:topic" component={LibraryPage} />
      <Route path="/peptide-overview" component={Home} />
      <Route path="/premium" component={PremiumContent} />
      
      {/* New pages for the 3-pathway redesign */}
      <Route path="/longevity-guild" component={LongevityGuild} />
      <Route path="/business-solutions" component={BusinessSolutions} />
      <Route path="/consulting" component={Consulting} />
      <Route path="/centenni-proposal" component={ProposalPDF} />
      <Route path="/proposals/plv052025" component={PLVProposal} />
      <Route path="/proposals/cactus052025" component={CactusProposal} />
      <Route path="/proposals/highmountmadison052025" component={HighMountMadisonProposal} />
      <Route path="/proposals/nexthealth052025" component={NextHealthProposal} />
      <Route path="/proposals/levey052025" component={Levey052025} />
      <Route path="/patients/onboarding" component={PatientOnboardingBasic} />
      <Route path="/patients-ff/onboarding" component={PatientOnboardingFF} />
      
      {/* Original peptide protocols implementation - still available but with updated categories */}
      <Route path="/premium/peptide-protocols" component={PeptideProtocols} />
      <Route path="/peptides/protocols" component={PeptideProtocols} />
      
      {/* Modular peptide protocols implementation - current version with better modularity */}
      <Route path="/premium/peptide-protocols-modular" component={PeptideProtocolsModular} />
      <Route path="/peptide-protocols-modular" component={PeptideProtocolsModular} />
      
      {/* Default route for peptide protocols redirects to modular version */}
      <Route path="/peptide-protocols" component={PeptideProtocolsModular} />
      <Route path="/peptide-assistant" component={PeptideAssistant} />
      <Route path="/mentorship" component={Mentorship} />
      
      {/* Specific mentorship section routes - MUST BE BEFORE CATCH-ALL ROUTES */}
      <Route path="/mentorship/lectures/cortisol-molecular-foundations" component={CortisolMolecularFoundations} />
      <Route path="/mentorship/lectures/questioning-gold-standard" component={QuestioningGoldStandard} />
      <Route path="/mentorship/lectures/cellular-basis-of-health" component={CellularBasisOfHealth} />
      <Route path="/mentorship/lectures/what-is-longevity-medicine" component={WhatIsLongevityMedicine} />
      <Route path="/mentorship/lectures/mitochondrial-health" component={MitochondrialHealth} />
      <Route path="/mentorship/lectures/epigenetics-cellular-medicine" component={EpigeneticsCellularMedicine} />
      <Route path="/mentorship/lectures/peroxisomes-health-disease" component={PeroxisomesHealthDisease} />
      <Route path="/mentorship/lectures/hormones-cellular-fundamentals" component={HormonesCellularFundamentals} />
      <Route path="/mentorship/lectures/testosterone-molecular-foundations" component={TestosteroneMolecularFoundations} />
      <Route path="/mentorship/lectures/testosterone-clinical-applications" component={TestosteroneClinicalApplications} />
      <Route path="/mentorship/lectures/estrogen-molecular-foundations" component={EstrogenMolecularFoundations} />
      <Route path="/mentorship/lectures/estrogen-clinical-applications" component={EstrogenClinicalApplications} />
      <Route path="/mentorship/lectures/thyroid-molecular-clinical" component={ThyroidMolecularClinical} />
      <Route path="/mentorship/lectures/ehr-platforms-for-longevity" component={EHRPlatformsForLongevity} />
      <Route path="/mentorship/lectures/glp1-agonists" component={GLP1Agonists} />
      <Route path="/mentorship/lectures/movement-nutrition-sleep" component={MovementNutritionSleep} />
      <Route path="/mentorship/lectures/inflammation" component={Inflammation} />
      <Route path="/mentorship/lectures/nac-clinical-application" component={NACClinicalApplication} />
      <Route path="/mentorship/lectures/anatomy-of-a-longevity-consult" component={AnatomyOfALongevityConsult} />
      <Route path="/mentorship/lectures/womens-longevity-interventions" component={WomensLongevityInterventions} />
      <Route path="/mentorship/lectures/evaluating-business-opportunities" component={EvaluatingBusinessOpportunities} />
      <Route path="/mentorship/lectures/cellular-framework-longevity" component={CellularFrameworkLongevity} />
      <Route path="/mentorship/lectures/shift-framework-health-longevity" component={ShiftFrameworkHealthLongevity} />
      <Route path="/mentorship/lectures/lifestyle-foundations" component={LifestyleFoundations} />
      <Route path="/mentorship/lectures/lifestyle-foundations-2" component={LifestyleFoundations2} />
      <Route path="/mentorship/lectures/lifestyle-foundations-3" component={LifestyleFoundations3} />
      <Route path="/mentorship/lectures/lifestyle-foundations-4" component={LifestyleFoundations4} />
      <Route path="/mentorship/lectures/lifestyle-foundations-5" component={LifestyleFoundations5} />

      <Route path="/mentorship/lectures/cardiovascular-disease-longevity" component={CardiovascularDiseaseLongevity} />
      <Route path="/mentorship/lectures/exosomes" component={Exosomes} />
      <Route path="/mentorship/lectures/long-covid" component={LongCovid} />
      <Route path="/mentorship/lectures/neurodegenerative-disease" component={NeurodegenerativeDisease} />
      <Route path="/mentorship/lectures/advanced-therapeutic-innovations" component={AdvancedTherapeuticInnovations} />
      <Route path="/mentorship/lectures/brain-health-neurofeedback" component={BrainHealthNeurofeedback} />
      <Route path="/mentorship/lectures/advanced-diagnostics" component={AdvancedDiagnostics} />
      <Route path="/mentorship/lectures/cellular-basis-health-disease" component={CellularBasisHealthDisease} />
      <Route path="/mentorship/lectures/functional-advanced-lab-testing" component={FunctionalAdvancedLabTesting} />
      <Route path="/mentorship/lectures/lifestyle-interventions" component={LifestyleInterventions} />
      <Route path="/mentorship/lectures/pharmaceuticals-repurposed-medications" component={PharmaceuticalsRepurposedMedications} />
      <Route path="/mentorship/lectures/microdosing-glp1s" component={MicrodosingGLP1s} />
      <Route path="/mentorship/lectures/microdosing-glp1s-2" component={MicrodosingGLP1s2} />
      <Route path="/mentorship/lectures/supplements-nutraceuticals" component={SupplementsNutraceuticals} />
      <Route path="/mentorship/lectures/peptides-shift-approach" component={PeptidesShiftApproach} />
      <Route path="/mentorship/lectures/taking-investment-selling-practice" component={TakingInvestmentSellingPractice} />
      <Route path="/mentorship/lectures/semaglutide-mechanism-precision-update" component={SemaglutideMechanismPrecisionUpdate} />
      <Route path="/mentorship/lectures/measuring-senescence-clinically" component={MeasuringSenescenceClinically} />

      <Route path="/mentorship/lectures/glp1s-and-longevity-atria-v3" component={GLP1sAndLongevityAtriaV3} />
      <Route path="/mentorship/lecture-generator" component={LectureGenerator} />
      <Route path="/mentorship/admin/lecture-manager" component={LectureManager} />
      <Route path="/mentorship/lectures/testosterone-embed-example" component={TestosteroneEmbedExample} />
      <Route path="/mentorship/lecture-studio" component={LectureStudio} />
      <Route path="/mentorship/lectures/long-covid" component={LongCovid} />
      <Route path="/mentorship/qa-archives/peptide-protocols" component={QAPeptideProtocols} />
      <Route path="/mentorship/business/practice-models" component={PracticeModels} />
      <Route path="/mentorship/business/telehealth" component={Telehealth} />
      <Route path="/mentorship/business/ehr-platforms" component={EHRPlatforms} />
      <Route path="/mentorship/longevity/insulin-resistance" component={InsulinResistance} />
      <Route path="/mentorship/payment/promotion0625" component={Promotion0625} />
      <Route path="/payment/promotion" component={PromotionPayment} />
      <Route path="/mentorship-content" component={MentorshipContent} />
      
      {/* Catch-all routes for mentorship section */}
      <Route path="/mentorship/:section/:topic" component={Mentorship} />
      <Route path="/mentorship/:section" component={Mentorship} />
      
      <Route path="/proposals" component={ProposalsPage} />
      <Route path="/login" component={Login} />
      <Route path="/signup" component={Signup} />
      <Route path="/post-payment-signup" component={PostPaymentSignup} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/slideshow" component={Slideshow} />
      <Route path="/longevity-2025" component={Longevity2025} />
      <Route path="/bryan-johnson-review" component={BryanJohnsonReview} />
      <Route path="/longevity-mistakes" component={LongevityMistakes} />
      <Route path="/longevity-lessons" component={LongevityLessons} />
      <Route path="/sample-male-hrt" component={SampleMaleHRT} />
      <Route path="/male-hrt-journal" component={MaleHRTJournal} />
      <Route path="/male-hrt-example" component={MaleHRTExample} />
      <Route path="/lectures/future-executive-health" component={FutureExecutiveHealth} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
